import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Underline from '@tiptap/extension-underline';
import { Box, Button, ButtonGroup, Divider, Paper } from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  Code,
  Undo,
  Redo,
  Link as LinkIcon,
  Image as ImageIcon,
} from '@mui/icons-material';

const RichTextEditor = ({ value, onChange, placeholder = 'İçerik girin...' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'rich-text-link',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'rich-text-image',
        },
      }),
      TextStyle,
      Color,
      Underline,
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html);
    },
    editorProps: {
      attributes: {
        class: 'rich-text-editor-content',
      },
    },
  });

  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  const addLink = () => {
    const url = window.prompt('Link URL\'si girin:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const addImage = () => {
    const url = window.prompt('Resim URL\'si girin:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  return (
    <Paper variant="outlined" sx={{ border: '1px solid', borderColor: 'divider' }}>
      <Box
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          p: 1,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          alignItems: 'center',
        }}
      >
        <ButtonGroup size="small" variant="text">
          <Button
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={!editor.can().chain().focus().toggleBold().run()}
            variant={editor.isActive('bold') ? 'contained' : 'text'}
            sx={{ minWidth: 40 }}
          >
            <FormatBold fontSize="small" />
          </Button>
          <Button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={!editor.can().chain().focus().toggleItalic().run()}
            variant={editor.isActive('italic') ? 'contained' : 'text'}
            sx={{ minWidth: 40 }}
          >
            <FormatItalic fontSize="small" />
          </Button>
          <Button
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            variant={editor.isActive('underline') ? 'contained' : 'text'}
            sx={{ minWidth: 40 }}
          >
            <FormatUnderlined fontSize="small" />
          </Button>
        </ButtonGroup>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <ButtonGroup size="small" variant="text">
          <Button
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            variant={editor.isActive('heading', { level: 1 }) ? 'contained' : 'text'}
            sx={{ minWidth: 40 }}
          >
            H1
          </Button>
          <Button
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            variant={editor.isActive('heading', { level: 2 }) ? 'contained' : 'text'}
            sx={{ minWidth: 40 }}
          >
            H2
          </Button>
          <Button
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            variant={editor.isActive('heading', { level: 3 }) ? 'contained' : 'text'}
            sx={{ minWidth: 40 }}
          >
            H3
          </Button>
        </ButtonGroup>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <ButtonGroup size="small" variant="text">
          <Button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            variant={editor.isActive('bulletList') ? 'contained' : 'text'}
            sx={{ minWidth: 40 }}
          >
            <FormatListBulleted fontSize="small" />
          </Button>
          <Button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            variant={editor.isActive('orderedList') ? 'contained' : 'text'}
            sx={{ minWidth: 40 }}
          >
            <FormatListNumbered fontSize="small" />
          </Button>
          <Button
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            variant={editor.isActive('blockquote') ? 'contained' : 'text'}
            sx={{ minWidth: 40 }}
          >
            <FormatQuote fontSize="small" />
          </Button>
          <Button
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            variant={editor.isActive('codeBlock') ? 'contained' : 'text'}
            sx={{ minWidth: 40 }}
          >
            <Code fontSize="small" />
          </Button>
        </ButtonGroup>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <ButtonGroup size="small" variant="text">
          <Button onClick={addLink} sx={{ minWidth: 40 }}>
            <LinkIcon fontSize="small" />
          </Button>
          <Button onClick={addImage} sx={{ minWidth: 40 }}>
            <ImageIcon fontSize="small" />
          </Button>
        </ButtonGroup>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <ButtonGroup size="small" variant="text">
          <Button
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().chain().focus().undo().run()}
            sx={{ minWidth: 40 }}
          >
            <Undo fontSize="small" />
          </Button>
          <Button
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().chain().focus().redo().run()}
            sx={{ minWidth: 40 }}
          >
            <Redo fontSize="small" />
          </Button>
        </ButtonGroup>
      </Box>
      <Box
        sx={{
          p: 2,
          minHeight: 200,
          maxHeight: 400,
          overflow: 'auto',
          '& .ProseMirror': {
            outline: 'none',
            minHeight: 150,
            '& p.is-editor-empty:first-child::before': {
              content: `"${placeholder}"`,
              float: 'left',
              color: 'text.disabled',
              pointerEvents: 'none',
              height: 0,
            },
            '& h1': {
              fontSize: '2em',
              fontWeight: 'bold',
              marginTop: '0.5em',
              marginBottom: '0.5em',
            },
            '& h2': {
              fontSize: '1.5em',
              fontWeight: 'bold',
              marginTop: '0.5em',
              marginBottom: '0.5em',
            },
            '& h3': {
              fontSize: '1.17em',
              fontWeight: 'bold',
              marginTop: '0.5em',
              marginBottom: '0.5em',
            },
            '& p': {
              margin: '0.5em 0',
            },
            '& ul, & ol': {
              paddingLeft: '1.5em',
              margin: '0.5em 0',
            },
            '& li': {
              margin: '0.25em 0',
            },
            '& blockquote': {
              borderLeft: '3px solid',
              borderColor: 'divider',
              paddingLeft: '1em',
              marginLeft: 0,
              marginRight: 0,
              fontStyle: 'italic',
              color: 'text.secondary',
            },
            '& code': {
              backgroundColor: 'action.hover',
              padding: '0.2em 0.4em',
              borderRadius: '3px',
              fontSize: '0.9em',
              fontFamily: 'monospace',
            },
            '& pre': {
              backgroundColor: 'action.hover',
              padding: '1em',
              borderRadius: '4px',
              overflow: 'auto',
              '& code': {
                backgroundColor: 'transparent',
                padding: 0,
              },
            },
            '& img': {
              maxWidth: '100%',
              height: 'auto',
              display: 'block',
              margin: '1em auto',
            },
            '& a': {
              color: 'primary.main',
              textDecoration: 'underline',
              '&:hover': {
                textDecoration: 'none',
              },
            },
            '& strong': {
              fontWeight: 'bold',
            },
            '& em': {
              fontStyle: 'italic',
            },
            '& u': {
              textDecoration: 'underline',
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Paper>
  );
};

export default RichTextEditor;

